import {
  getPublicProfile,
  getSocialList,
  getPublicGoals,
  getPublicCalendarContributions,
} from '../api/userApi';
import UserGlobalYearCalendar from '../usercomponents/UserGlobalYearCalendar';
import UserRankPanel from '../components/rank/UserRankPanel';
import UserGoalCard from '../usercomponents/UserGoalCard';
import { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import '../Style/PublicProfile.css';
import { useGoalActions } from '../hooks/useGoalActions';
import { useRef } from 'react';
import SocialModal from '../components/SocialModal';
import GoalPrivacyManager from '../components/GoalPrivacyManager';
import { getAchievements } from '../api/userGoalApi';
import { updateGoalPrivacy } from '../api/userGoalApi';
import { useUserActions } from '../hooks/useUserActions';
import {
  toggleProfilePrivacy,
  cancelFollowRequest,
  followUser,
  unfollowUser,
} from '../api/userFollowApi';
import ReportModal from '../components/ReportModal';
import GoalReminderManager from '../components/GoalReminderManager';

const PublicProfile = () => {
  const [loading, setLoading] = useState(true);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [openGoals, setOpenGoals] = useState({});
  const { currentUser, authLoading } = useContext(AuthContext); // Get the user!
  const isOwnProfile = currentUser && String(currentUser.id) === String(userId);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [userList, setUserList] = useState([]);
  const [viewedMonths, setViewedMonths] = useState({});
  const loadingGoalLock = useRef({});
  const [goalDetails, setGoalDetails] = useState({});
  const [goalCheckDates, setGoalCheckDates] = useState({});
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [myAchievements, setMyAchievements] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);

  const { handleUpdateBio } = useUserActions({
    setProfile,
    setMyAchievements,
  });

  useEffect(() => {
    if (isOwnProfile) {
      getAchievements().then((res) => {
        setMyAchievements(Array.isArray(res.data) ? res.data : []);
      });
    }
  }, [isOwnProfile]);

  const handleFollowStatusChange = (changedUserId, isNowFollowing) => {
    console.log('Syncing change for User:', changedUserId, 'Status:', isNowFollowing);

    setProfile((prev) => {
      if (!prev) return prev;
      const newProfile = { ...prev };

      // CASE 1: You followed the person whose profile you are actually visiting
      // (This updates their "Followers" count)
      if (String(changedUserId) === String(userId)) {
        newProfile.isFollowing = isNowFollowing;
        newProfile.followersCount = isNowFollowing
          ? prev.followersCount + 1
          : Math.max(0, prev.followersCount - 1);
      }

      // CASE 2: You followed someone while looking at YOUR OWN profile
      // (This updates your "Following" count)
      if (isOwnProfile) {
        newProfile.followingCount = isNowFollowing
          ? prev.followingCount + 1
          : Math.max(0, prev.followingCount - 1);
      }

      return newProfile;
    });
  };

  const handleFollowToggle = async () => {
    const previousState = {
      isFollowing: profile.isFollowing,
      followersCount: profile.followersCount,
      requestSent: profile.requestSent,
    };

    try {
      if (profile.isFollowing) {
        // Unfollow
        setProfile((prev) => ({
          ...prev,
          isFollowing: false,
          followersCount: prev.followersCount - 1,
        }));
        await unfollowUser(userId);
      } else if (profile.requestSent) {
        // Cancel pending request
        setProfile((prev) => ({ ...prev, requestSent: false }));
        await cancelFollowRequest(userId);
      } else {
        // Follow or request
        const result = await followUser(userId);
        if (result?.status === 'REQUESTED') {
          setProfile((prev) => ({ ...prev, requestSent: true }));
        } else {
          setProfile((prev) => ({
            ...prev,
            isFollowing: true,
            followersCount: prev.followersCount + 1,
          }));
        }
      }
    } catch (error) {
      // Rollback on failure
      setProfile((prev) => ({ ...prev, ...previousState }));
      console.error('Follow toggle failed', error);
    }
  };

  const { handleTogglePrivacyForAll, handleMonthNav, handleOpenList, handleViewGoal } =
    useGoalActions({
      setGoals: (updatedGoalsOrFn) => {
        setProfile((prev) => {
          if (!prev) return prev;

          const newGoals =
            typeof updatedGoalsOrFn === 'function'
              ? updatedGoalsOrFn(prev.goals)
              : updatedGoalsOrFn;

          return { ...prev, goals: newGoals };
        });
      },
      setOpenGoals,
      setGoalDetails,
      setGoalCheckDates,
      setViewedMonths,
      loadingGoalLock,
      setShowModal,
      setModalTitle,
      setUserList,
      setProfile,
      setMyAchievements,

      // Add these
      publicUserId: userId,
      isPublicProfile: true,
    });

  useEffect(() => {
    if (!authLoading && currentUser && userId) {
      fetchProfile();
    }
  }, [userId, authLoading, currentUser]);

  const fetchProfile = async () => {
    setLoading(true);

    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const today = new Date();
      const to = today.toLocaleDateString('en-CA');

      const fromDate = new Date();
      fromDate.setFullYear(today.getFullYear() - 1);
      const from = fromDate.toLocaleDateString('en-CA');

      // 1. Load lightweight profile first
      const profileData = await getPublicProfile(userId, tz);

      setProfile({
        ...profileData,
        goals: profileData.goals || [],
        calendarData: profileData.calendarData || [],
      });

      setLoading(false);

      // 2. Load goals + calendar separately
      const [publicGoals, calendarData] = await Promise.all([
        getPublicGoals(userId),
        getPublicCalendarContributions(userId, from, to, tz),
      ]);

      setProfile((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          goals: Array.isArray(publicGoals) ? publicGoals : [],
          calendarData: Array.isArray(calendarData) ? calendarData : [],
        };
      });
    } catch (error) {
      console.error('Error fetching profile', error);
      setLoading(false);
    }
  };

  const handleToggleProfilePrivacy = async () => {
    try {
      const result = await toggleProfilePrivacy();
      setProfile((prev) => ({ ...prev, isPrivate: result.isPrivate }));
    } catch (err) {
      console.error('Failed to toggle profile privacy', err);
    }
  };

  const handleReminderUpdated = (updatedGoal) => {
    setProfile((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        goals: (prev.goals || []).map((goal) =>
          goal.id === updatedGoal.id ? { ...goal, ...updatedGoal } : goal,
        ),
      };
    });
  };

  if (loading) return <div className="app-container">Loading Profile...</div>;
  if (!profile) return <div className="app-container">User not found.</div>;

  const isFollowingOrOwner = isOwnProfile || profile.isFollowing;
  const isPrivateAndBlocked = profile.isPrivate && !isFollowingOrOwner;

  return (
    <div className="app-container public-profile-page">
      {!isOwnProfile && (
        <button
          className="report-user-btn"
          onClick={() => setShowReportModal(true)}
          title="Report this user"
        >
          ⚠️ Report
        </button>
      )}

      {isOwnProfile && (
        <>
          {showPrivacyModal && (
            <GoalPrivacyManager
              profile={profile}
              goals={profile.goals || []}
              achievements={myAchievements}
              onUpdateBio={handleUpdateBio}
              onTogglePrivacy={handleTogglePrivacyForAll}
              onToggleProfilePrivacy={handleToggleProfilePrivacy}
              onClose={() => setShowPrivacyModal(false)}
            />
          )}

          {showReminderModal && (
            <GoalReminderManager
              goals={profile.goals || []}
              onReminderUpdated={handleReminderUpdated}
              onClose={() => setShowReminderModal(false)}
            />
          )}

          <div className="floating-profile-actions">
            <button className="floating-privacy-btn" onClick={() => setShowPrivacyModal(true)}>
              ⚙️ Privacy Settings
            </button>

            <button className="floating-reminder-btn" onClick={() => setShowReminderModal(true)}>
              🔔 Reminder Settings
            </button>
          </div>
        </>
      )}
      <header className="profile-view-header">
        {profile?.profilePicUrl ? (
          <img src={profile.profilePicUrl} alt="Profile" className="profile-pic" />
        ) : (
          <div className="profile-pic-placeholder">
            <span>👤</span>
          </div>
        )}
        <div className="profile-info-stack">
          <div>
            <h1 className="profile-name-text">
              {profile.firstName} {profile.lastName}
            </h1>
            {profile?.bio && <p className="profile-bio-display">{profile.bio}</p>}
          </div>

          <div className="stats-mini" style={{ display: 'flex', alignItems: 'center' }}>
            <span className="clickable-stat" onClick={() => handleOpenList(userId, 'followers')}>
              <strong>{profile.followersCount}</strong> Followers
            </span>

            <span className="following-stat" onClick={() => handleOpenList(userId, 'following')}>
              <strong>{profile.followingCount}</strong> Following
            </span>

            {/* Wrap the button so its height doesn't affect the parent row */}
            {!isOwnProfile && (
              <button
                type="button"
                onClick={handleFollowToggle}
                className={`profile-follow-inline-btn ${
                  profile.isFollowing ? 'btn-secondary' : 'btn-primary'
                }`}
              >
                {profile.isFollowing ? 'Unfollow' : profile.requestSent ? 'Requested' : 'Follow'}
              </button>
            )}
          </div>
        </div>
        <div className="profile-header-actions">
          {!isPrivateAndBlocked && (
            <Link
              to={isOwnProfile ? '/achievements' : `/profile/${userId}/achievements`}
              className="achievement-link-btn"
              title="View Achievement Vault"
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#fbbf24';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <span style={{ fontSize: '1.4rem' }}>🏆</span>
            </Link>
          )}
        </div>
      </header>

      {/* 2. Reuse the Rank Panel */}
      <UserRankPanel
        progress={{
          currentRank: profile.rank,
          totalXP: profile.totalXp,
          dailyXP: profile.dailyXP, // Publicly we might not track daily XP, or you can add it to DTO
          // We use the rankProgress from the DTO to simulate the bar if needed
          rankProgress: null,
        }}
      />
      {/* 3. Reuse the Global Heatmap */}
      {!isPrivateAndBlocked ? (
        <>
          <h3 style={{ fontSize: '1.1rem', marginBottom: '12px' }}>Activity</h3>
          <UserGlobalYearCalendar contributions={profile.calendarData || []} />

          <h3 style={{ fontSize: '1.1rem', margin: '30px 0 12px' }}>Public Goals</h3>
          <ul className="goal-list">
            {profile.goals &&
              profile.goals.map((goal) => {
                const today = new Date().toLocaleDateString('en-CA');
                const isDoneToday = goal.checkDates?.some((dateString) => {
                  const formattedDate = dateString.split('T')[0];
                  return formattedDate === today;
                });
                return (
                  <li key={goal.id}>
                    <UserGoalCard
                      goal={goal}
                      isReadOnly={true}
                      checkDates={goalCheckDates[goal.id] || goal.checkDates || []}
                      onView={() => handleViewGoal(goal.id, 'publicView')}
                      onMarkDoneToday={null}
                      selectedGoal={goalDetails[goal.id] || goal}
                      isOpen={openGoals[goal.id]}
                      viewedMonth={viewedMonths[goal.id]}
                      onNextMonth={() => handleMonthNav(goal.id, 'NEXT')}
                      onPrevMonth={() => handleMonthNav(goal.id, 'PREV')}
                      isDoneToday={isDoneToday}
                    />
                  </li>
                );
              })}
            {goalsLoading ? (
              <p className="empty-entries">Loading goals...</p>
            ) : !profile.goals || profile.goals.length === 0 ? (
              <p className="empty-entries">No public goals to show.</p>
            ) : null}
          </ul>
        </>
      ) : (
        <div className="private-profile-block">
          <span className="private-profile-icon">🔒</span>
          <p>This account is private.</p>
          <p>Follow this user to see their goals and activity.</p>
        </div>
      )}

      <SocialModal
        isOpen={showModal}
        title={modalTitle}
        userList={userList}
        setUserList={setUserList}
        onClose={() => setShowModal(false)}
        currentUserId={currentUser?.id}
        onFollowStatusChange={handleFollowStatusChange}
        isOwnFollowersList={isOwnProfile && modalTitle === 'Followers'}
      />
      {showReportModal && (
        <ReportModal
          reportedId={userId}
          reportedName={`${profile.firstName} ${profile.lastName}`}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
};

export default PublicProfile;
